import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";
import type { Role } from "@shared/types";
import { can, type Capability } from "@shared/permissions";

export interface Me {
  id: string;
  org_id: string;
  email: string;
  name: string;
  partner_id: string | null;
  auto_logout_minutes: number | null;
  roles: Role[];
}

interface AuthCtx {
  user: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasCap: (cap: Capability) => boolean;
  hasRole: (...roles: Role[]) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>(null as unknown as AuthCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { user } = await api.get<{ user: Me | null }>("/auth/me");
      setUser(user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email: string, password: string) => {
    await api.post("/auth/login", { email, password });
    await refresh();
  };
  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };
  const hasCap = (cap: Capability) => !!user && user.roles.some((r) => can(r, cap));
  const hasRole = (...roles: Role[]) => !!user && user.roles.some((r) => roles.includes(r));

  return (
    <Ctx.Provider value={{ user, loading, login, logout, hasCap, hasRole, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
